import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Alert,
  FormControlLabel,
  Switch,
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import { Edit, Delete, Add } from '@mui/icons-material';
import { BASE_URL } from '../config';
import { useAuth } from '../context/AuthContext';

interface Skill {
  id: number;
  nome: string;
  categoria: string | null;
  descrizione: string | null;
  enabled: boolean;
  created_at?: string;
  updated_at?: string;
}

const SkillsPage: React.FC = () => {
  const { hasPermission } = useAuth();
  const [skills, setSkills] = useState<Skill[]>([]);
  const [categorie, setCategorie] = useState<string[]>([]);
  const [categoriaFilter, setCategoriaFilter] = useState<string>('all');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingSkill, setEditingSkill] = useState<Skill | null>(null);
  const [formData, setFormData] = useState<Partial<Skill>>({
    enabled: true,
  });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadSkills();
    loadCategorie();
  }, []);

  const loadSkills = async () => {
    try {
      const userStr = sessionStorage.getItem('user');
      const user = userStr ? JSON.parse(userStr) : null;

      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      if (user?.id) {
        headers['x-user-id'] = user.id.toString();
      }

      const response = await fetch(`${BASE_URL}/skills?includeDisabled=true`, { headers });
      if (!response.ok) {
        console.error('Failed to load skills');
        setSkills([]);
        setError('Errore nel caricamento delle skills');
        return;
      }
      const data = await response.json();
      setSkills(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading skills:', err);
      setSkills([]);
      setError('Errore nel caricamento delle skills');
    }
  };

  const loadCategorie = async () => {
    try {
      const userStr = sessionStorage.getItem('user');
      const user = userStr ? JSON.parse(userStr) : null;

      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      if (user?.id) {
        headers['x-user-id'] = user.id.toString();
      }

      const response = await fetch(`${BASE_URL}/skills/categorie`, { headers });
      if (response.ok) {
        const data = await response.json();
        setCategorie(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error('Error loading categorie:', err);
    }
  };

  const handleOpenDialog = (skill?: Skill) => {
    if (skill) {
      setEditingSkill(skill);
      setFormData(skill);
    } else {
      setEditingSkill(null);
      setFormData({ enabled: true });
    }
    setOpenDialog(true);
    setError(null);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingSkill(null);
    setFormData({ enabled: true });
    setError(null);
  };

  const handleSave = async () => {
    try {
      if (!formData.nome || !formData.nome.trim()) {
        setError('Nome skill obbligatorio');
        return;
      }

      const userStr = sessionStorage.getItem('user');
      const user = userStr ? JSON.parse(userStr) : null;

      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      if (user?.id) {
        headers['x-user-id'] = user.id.toString();
      }

      const url = editingSkill
        ? `${BASE_URL}/skills/${editingSkill.id}`
        : `${BASE_URL}/skills`;

      const method = editingSkill ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers,
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Errore nel salvataggio');
      }

      loadSkills();
      loadCategorie();
      handleCloseDialog();
    } catch (err: any) {
      setError(err.message || 'Errore nel salvataggio');
    }
  };

  const handleDelete = async (id: number, nome: string) => {
    if (!window.confirm(`Eliminare la skill "${nome}"?`)) return;

    try {
      const userStr = sessionStorage.getItem('user');
      const user = userStr ? JSON.parse(userStr) : null;

      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      if (user?.id) {
        headers['x-user-id'] = user.id.toString();
      }

      const response = await fetch(`${BASE_URL}/skills/${id}`, {
        method: 'DELETE',
        headers,
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Errore nell\'eliminazione');
      }

      loadSkills();
    } catch (err: any) {
      alert(err.message || 'Errore nell\'eliminazione');
    }
  };

  const filteredSkills = categoriaFilter === 'all'
    ? skills
    : skills.filter(s => s.categoria === categoriaFilter);

  const groupedSkills = filteredSkills.reduce((acc, skill) => {
    const cat = skill.categoria || 'Senza Categoria';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(skill);
    return acc;
  }, {} as Record<string, Skill[]>);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight="bold">
          Skills / Competenze
        </Typography>
        {hasPermission('risorse', 'create') && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
          >
            Nuova Skill
          </Button>
        )}
      </Box>

      {error && !openDialog && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 3 }}>
        <ToggleButtonGroup
          value={categoriaFilter}
          exclusive
          onChange={(_, value) => value && setCategoriaFilter(value)}
          size="small"
        >
          <ToggleButton value="all">Tutte</ToggleButton>
          {categorie.map(cat => (
            <ToggleButton key={cat} value={cat}>{cat}</ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      {Object.entries(groupedSkills).map(([categoria, skillsInCategoria]) => (
        <Paper key={categoria} sx={{ mb: 3 }}>
          <Box sx={{ p: 2, bgcolor: 'primary.main', color: 'white' }}>
            <Typography variant="h6">{categoria}</Typography>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Nome</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Descrizione</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="center">Stato</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: 120 }}>Azioni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {skillsInCategoria.map((skill) => (
                  <TableRow key={skill.id} hover>
                    <TableCell>{skill.nome}</TableCell>
                    <TableCell>{skill.descrizione || '-'}</TableCell>
                    <TableCell align="center">
                      {skill.enabled ? (
                        <Chip label="Attiva" color="success" size="small" />
                      ) : (
                        <Chip label="Disattiva" color="default" size="small" />
                      )}
                    </TableCell>
                    <TableCell>
                      {hasPermission('risorse', 'update') && (
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleOpenDialog(skill)}
                        >
                          <Edit />
                        </IconButton>
                      )}
                      {hasPermission('risorse', 'delete') && (
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(skill.id, skill.nome)}
                        >
                          <Delete />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      ))}

      {/* Dialog per creazione/modifica */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingSkill ? 'Modifica Skill' : 'Nuova Skill'}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="Nome Skill"
            type="text"
            fullWidth
            required
            value={formData.nome || ''}
            onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
            sx={{ mt: 2 }}
          />
          <FormControl fullWidth margin="dense" sx={{ mt: 2 }}>
            <InputLabel>Categoria</InputLabel>
            <Select
              value={formData.categoria || ''}
              label="Categoria"
              onChange={(e) => setFormData({ ...formData, categoria: e.target.value })}
            >
              <MenuItem value="">
                <em>Nessuna</em>
              </MenuItem>
              <MenuItem value="Backend Development">Backend Development</MenuItem>
              <MenuItem value="Frontend Development">Frontend Development</MenuItem>
              <MenuItem value="Database">Database</MenuItem>
              <MenuItem value="Mobile Development">Mobile Development</MenuItem>
              <MenuItem value="IoT">IoT</MenuItem>
              <MenuItem value="Architecture">Architecture</MenuItem>
              <MenuItem value="Management">Management</MenuItem>
            </Select>
          </FormControl>
          <TextField
            margin="dense"
            label="Descrizione"
            type="text"
            fullWidth
            multiline
            rows={3}
            value={formData.descrizione || ''}
            onChange={(e) => setFormData({ ...formData, descrizione: e.target.value })}
            sx={{ mt: 2 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.enabled !== false}
                onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
              />
            }
            label="Attiva"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Annulla</Button>
          <Button onClick={handleSave} variant="contained">
            Salva
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SkillsPage;
